import axiosInstance from "@/lib/axios";
import { whatsappChatsEndpoint } from "@/config/endpoints";
import { truncate } from "@/utils/truncate";
import { useInfiniteQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { getInitials } from "@/utils/convetors";
import { getRelativeTime } from "@/utils/date";
import type { ChatChannel } from "../types/whatsapp.types";

// ==========================================
// 1. THE ADAPTER (Refined for Chats)
// ==========================================

// Stable color generator so avatars don't "flicker" colors
const getStableColor = (name: string) => {
  const colors = [
    "#008069", // 1. Official Primary Teal (Header Color)
    "#00a884", // 2. WhatsApp Web Green (Active State)
    "#128C7E", // 3. Dark Teal (Outgoing Bubble/Business)
    "#25D366", // 4. Logo Green (Notifications/Badges)
    "#34B7F1", // 5. Tick Blue (Links/Read Receipts)
    "#607D8B", // 6. Blue Grey (System/Archived - Professional)
    "#535c68", // 7. Muted Slate (Neutral User)
    "#8e44ad", // 8. Material Purple (Common WhatsApp Default Avatar)
  ];
  const hash = name
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export const adaptChat = (chat: any) => {
  const lead = chat.lead;
  const name = lead?.name || "Unknown";
  const windowExpiresAt = chat.windowExpiresAt || null;
  const windowAlwaysOpen = chat.windowAlwaysOpen === true;
  const isExpired = windowAlwaysOpen
    ? false
    : windowExpiresAt
      ? new Date(windowExpiresAt).getTime() <= Date.now()
      : true;

  const assignedUsers = (lead?.LeadUsers || []).map((lu: any) => ({
    name: lu.user?.name || "Unknown",
  }));

  return {
    id: chat.id,
    leadId: chat.leadId,
    whatsappAccountId: chat.whatsappAccountId ?? null,
    // Which transport owns this thread. BSP-mirrored chats (Wati/Gallabox)
    // carry a leadSourceId instead of a whatsappAccountId, so anything keyed on
    // the account must branch on this rather than assume Cloud API.
    provider: chat.provider ?? "CLOUD_API",
    leadSourceId: chat.leadSourceId ?? null,
    // WhatsApp, or an Instagram / Messenger thread mirrored from Wati. Drives
    // the badge, the reply window and whether templates apply.
    channel: (chat.channel ?? "WHATSAPP") as ChatChannel,
    avatar: getInitials(name),
    color: getStableColor(name),
    name: truncate(name, 25),
    lastMessage: truncate(chat.lastMessage || "No messages yet", 35),
    lastMessageType: (chat.lastMsgType || "text").toLowerCase(),
    time: getRelativeTime(chat.lastMessageAt),
    phone: lead?.phone || "",
    unread: chat.unreadCount || 0,
    windowExpiresAt,
    windowAlwaysOpen,
    isExpired,
    stage: lead?.stage?.name || undefined,
    status: lead?.status?.name || undefined,
    subStatus: lead?.subStatus?.name || undefined,
    assignedTo: assignedUsers[0]?.name || undefined,
    assignedUsers,
    // Undelivered rollup. Automations send most of the outbound traffic and
    // nobody is watching the thread when they fail, so without this the row
    // looks identical whether the greeting reached the lead or not.
    failedCount: Number(chat.failedCount) || 0,
    lastFailure: chat.lastFailure ?? null,
  };
};

// ==========================================
// 2. THE HOOK (Optimized Infinite Scroll)
// ==========================================

/**
 * Chats per request. The server clamps this to 100; 30 fills the sidebar
 * roughly twice over on a laptop, so the first page always overflows the
 * viewport and the scroll-to-load handler has something to trigger on.
 */
export const CHAT_PAGE_SIZE = 30;

/** One page of `GET /whatsapp/chats`. */
type ChatsPage = {
  chats?: unknown[];
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
    hasMore?: boolean;
    /** Non-WhatsApp networks the workspace has chats on (first page only). */
    otherChannels?: ChatChannel[];
  };
};

export function useWhatsappContacts(filters?: any) {
  const {
    data,
    isLoading,
    refetch,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    // Changed key to "chats" for clarity
    queryKey: ["whatsapp-chats", filters],

    queryFn: async ({ pageParam = 1 }) => {
      const response = await axiosInstance.get(whatsappChatsEndpoint, {
        params: {
          ...filters,
          page: pageParam,
          limit: CHAT_PAGE_SIZE,
        },
      });
      return response.data; // { chats: [], meta: { total, page, limit, hasMore } }
    },

    initialPageParam: 1,

    getNextPageParam: (lastPage: ChatsPage, allPages) => {
      // `hasMore` is authoritative (the server computes it against the same
      // scoped count). The length fallback covers an older server build that
      // returns no meta at all — without it this hook silently reports
      // "no more pages" and the inbox ends at page 1, which is exactly the
      // bug this pagination exists to fix.
      const pageChats = lastPage?.chats || [];
      const hasMore =
        typeof lastPage?.meta?.hasMore === "boolean"
          ? lastPage.meta.hasMore
          : pageChats.length >= CHAT_PAGE_SIZE;

      return hasMore ? allPages.length + 1 : undefined;
    },

    // Optimized select to flatten and adapt in one go
    select: (data) => {
      // Dedupe by chat id: pages are offset-based over a live "newest message
      // first" sort, so a chat that receives a message between two page
      // requests can land in both. React keys on `id`, so leaving the dupe in
      // renders the same conversation twice in the sidebar.
      const pages = data.pages as ChatsPage[];
      const seen = new Set<number>();
      const allChats: ReturnType<typeof adaptChat>[] = [];
      for (const page of pages) {
        for (const raw of page?.chats || []) {
          const chat = adaptChat(raw);
          if (seen.has(chat.id)) continue;
          seen.add(chat.id);
          allChats.push(chat);
        }
      }

      return {
        pages: data.pages,
        pageParams: data.pageParams,
        // Flattened list for the UI
        allChats,
        total: Number(pages[0]?.meta?.total) || allChats.length,
        otherChannels: pages[0]?.meta?.otherChannels ?? [],
      };
    },
  });

  return {
    chats: data?.allChats || [],
    // Total conversations the user can see, not just the loaded ones — the
    // sidebar header count would otherwise read "30" on a 400-chat inbox.
    totalChats: data?.total ?? 0,
    otherChannels: data?.otherChannels ?? ([] as ChatChannel[]),
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    refetch,
    error,
    isFetchingNextPage,
  };
}

// Global helper to refresh the inbox (e.g. after sending a message)
export const refetchWhatsappLeads = () => {
  queryClient.invalidateQueries({ queryKey: ["whatsapp-chats"] });
};
